import qs from 'qs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useItemsApi } from './useItemsApi';
import type { BiddingInfoResult, BiddingInfoResultResponse, BiddingSchedulesRequest, BiddingSchedulesResponse, GetItemsFilters, SystemMaster } from '../types'; // 型をインポート
import { useCallback, useState } from 'react';
import { showApiErrorToast, type MutationConfig } from './util/commonMutation';
import { ListingCategoryConst, PrefTypeConst, type PrefectureType } from '../constants';
import toast from 'react-hot-toast';
import { CSVOutputConst, type CSVOutputType } from '../constants/CSVOutputConst';

export const useBiddingResultGetAllResultListQuery = (filters: GetItemsFilters, loginId: string, options?: { enabled?: boolean }) => {
  const { biddingGetAllResultList } = useItemsApi();
  return useQuery<BiddingInfoResultResponse, Error>({ // 成功時とエラー時の型を指定
    queryKey: ['biddinginfo', 'allresult', filters, loginId ],
    queryFn: async () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });
      const response = await biddingGetAllResultList(queryString); 
      
      return response;
    },
    // 受け取った enabled の値を useQuery に適用する
    // (loginId があるときだけ、かつ options.enabled が false でないときだけ実行)
    enabled: options?.enabled !== undefined ? options.enabled : !!loginId,
    
    //他のオプションが来ても大丈夫なように広げておく
    ...options,
  });
};

// 監視画面用の上記入札結果&出品情報の取得複製フック（10秒更新対応版）
export const useBiddingMonitorQuery = (
  filters: GetItemsFilters, 
  loginId: string, 
  isPollingEnabled: boolean = true
) => {
  const { biddingGetMonitorList } = useItemsApi();

  return useQuery<BiddingInfoResultResponse, Error>({
    queryKey: ['biddingmonitor', 'realtime', filters, loginId], 
    queryFn: async () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });
      
      const response = await biddingGetMonitorList(queryString); 
      
      return {
        ...response,
        items: response.items || []
      };
    },
    refetchInterval: isPollingEnabled ? 10000 : false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
};

export const useBiddingAdminQuery = (
  filters: GetItemsFilters, 
  loginId: string, 
) => {
  const { biddingGetAllResultList } = useItemsApi();

  return useQuery<BiddingInfoResult[], Error>({
    // キャッシュが混ざらないようキー名 ('biddingadmin',) を設定
    queryKey: ['biddingadmin', filters, loginId], 
    queryFn: async () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });
      // ▼ APIからは「箱(オブジェクト)」が返ってくる
      const response = await biddingGetAllResultList(queryString); 
      
      // ▼ 箱の中から「配列(.items)」だけを取り出して返す！
      return response.items || [];
    },
  });
};

export const useBiddingReportQuery = (
  filters: GetItemsFilters, 
  loginId: string, 
  options?: { enabled?: boolean }
) => {
  const { biddingGetAllResultListForReport } = useItemsApi();

  return useQuery<BiddingInfoResult[], Error>({
    // キャッシュが混ざらないようキー名 ('biddingreport',) を設定
    queryKey: ['biddingreport', filters, loginId], 
    queryFn: async () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });
      // ▼ APIからは「箱(オブジェクト)」が返ってくる
      const response = await biddingGetAllResultListForReport(queryString); 
      
      // ▼ 箱の中から「配列(.items)」だけを取り出して返す！
      return response.items || [];
    },
    ...options,
  });
};

export const useBiddingResultGetListQuery = (
  filters: GetItemsFilters,
  loginId: string,
  options?: { enabled?: boolean }
) => {
  const { biddingResultGetList } = useItemsApi();

  return useQuery<BiddingInfoResultResponse, Error>({
    queryKey:['biddingResultConfirm', filters, loginId],
    queryFn: async () => {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });
      // ▼ APIからは「箱(オブジェクト)」が返ってくる
      const response = await biddingResultGetList(queryString); 
      return response;
    },
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });
};

/**
 * 落札確定（スケジュールに落札確定データを登録）
 */
export const useBiddingScheduleConfirmMutation = (
  filters: GetItemsFilters,
  loginId: string,
) => {
  type mutationConfig = MutationConfig<BiddingSchedulesResponse, Error, BiddingSchedulesRequest>
  const { createSchedulesConfirm } = useItemsApi();
  const queryClient = useQueryClient();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationKey:['biddingResultConfirm', filters, loginId],
    mutationFn: (variables) =>
      createSchedulesConfirm(variables),
      onSuccess: () => {
        // 成功時の処理
          toast.success("落札確定処理が完了しました。", {duration: 5000});

          queryClient.invalidateQueries({ 
            queryKey: ['biddingResultConfirm'] 
          });
      },
      onError: (error) => {
        showApiErrorToast(error, error.message)
      }
  });
};

/**
 * 落札確定取消（スケジュールから落札確定データを削除）
 * @returns 
 */
export const useBiddingSchedulesConfirmCancelMutation = (
  filters: GetItemsFilters,
  loginId: string,
) => {
  type mutationConfig = MutationConfig<BiddingSchedulesResponse, Error, BiddingSchedulesRequest>
  const { deleteSchedulesConfirm } = useItemsApi();
  const queryClient = useQueryClient();

  return useMutation<
    mutationConfig["ret"],
    mutationConfig["error"],
    mutationConfig["variables"]
  >({
    mutationKey:['biddingResultConfirm', filters, loginId],
    mutationFn: (variables) =>
      deleteSchedulesConfirm(variables),
      onSuccess: () => {
        toast.success("落札確定取消が完了しました。", {duration: 5000});

        queryClient.invalidateQueries({ 
          queryKey: ['biddingResultConfirm'] 
        });
      },
      onError: (error) => {
        showApiErrorToast(error, error.message)
      }
  });
};

export const useSystemMasterQuery = () => {
  const { getSystemMaster } = useItemsApi();

  return useQuery<SystemMaster, Error>({
    queryKey: ['systemMaster'],
    queryFn: getSystemMaster,
  });
}

/**
 * WebView2(C#)からの特定のメッセージを Promise で待機するヘルパー関数
 */
const waitForWebViewMessage = (expectedType: string, timeoutMs: number = 20000): Promise<void> => {
  return new Promise((resolve, reject) => {
    const handleMessage = (event: any) => {
      // C#側の WebViewResponse クラスのプロパティ (Type, IsSuccess, Message, Data) に合わせて判定します
      const data = event.data;
      
      // C#側で response.Type = "Confirm" の内容でくることを想定
      if (data?.Type === expectedType) {
        window.chrome?.webview?.removeEventListener('message', handleMessage);

        if (data?.IsSuccess === true) {
          resolve();
        } else {
          reject(new Error(data?.Message || "C#側の処理でエラーが発生しました。"));
        }
      }
    };

    const webview = window.chrome?.webview;
    if (webview) {
      webview.addEventListener('message', handleMessage);
      
      // タイムアウト処理（C#から応答がない場合に無限待ちになるのを防ぐ）
      setTimeout(() => {
        webview.removeEventListener('message', handleMessage);
        reject(new Error("WebView側からの応答がタイムアウトしました。"));
      }, timeoutMs);
    } else {
      reject(new Error("WebView環境ではありません。"));
    }
  });
};

/**
 * 落札確定処理用カスタムフック
 * @param items 
 * @param prefectureType 
 * @returns 
 */
export const useBiddingConfirmMutation = (itemFilters:GetItemsFilters, loginId:string | "", options?: { onSuccess?: () => void }) => {
  // 落札確定処理実行中フラグ
  const [isConfirm, setConfirm] = useState(false);
  // エラー状態管理
  const [error, setError] = useState<string | null>(null);

  // --- 共通終了処理 ---
  const finalizeConfirm = useCallback(() => {
    setConfirm(false);
    document.body.style.cursor = 'default';
  }, []);

  const { mutateAsync: confirmSchedule } = useBiddingScheduleConfirmMutation(itemFilters, loginId);
  const { refetch: refetchSystemMaster } = useSystemMasterQuery();

  /**
  * 落札確定プロセス
  */
  const executeConfirmProcess = useCallback(async (items: BiddingInfoResult[] | undefined, prefectureType: PrefectureType) => {
    // 処理中の場合は実行しない
    if (isConfirm) return;
    // データが空の場合は実行しない
    if (!items || items.length === 0) return;
    
    setConfirm(true);
    setError(null);
    document.body.style.cursor = 'wait';

    try{
      if (window.chrome?.webview) {

        const { data: systemMaster, isError } = await refetchSystemMaster();

        if(isError || !systemMaster)
        {
          toast.error("システムマスタの取得に失敗しました。", {duration:5000});
          return;
        }
        else if(systemMaster?.coreLinkFolderPath === null || systemMaster?.coreLinkFolderPath == ""){
          toast.error("基幹連携用CSV出力先フォルダを確認できませんでした。\nシステムマスタを確認してください。", {duration:5000});
          return;
        }
        else if (systemMaster?.signageFolderPath === null || systemMaster?.signageFolderPath == "") {
          toast.error("サイネージ連携用CSV出力先フォルダを確認できませんでした。\nシステムマスタを確認してください。", {duration:5000});
          return;
        }

        // リクエストデータ作成
        const request: BiddingSchedulesRequest = { 
          Date:parseInt(items[0].biddingDate),
          ListingCategory:items[0].listingCategory, 
          PrefectureType: prefectureType
        };

        // API実行
        const response =  await confirmSchedule(request);

        /**
         * C#側のインターフェース呼び出し
         * 共通通信プロトコル（PageName, Function, Payload）に準拠して送信
         */
        window.chrome.webview.postMessage({
          PageName: "Confirm",
          Function: "Confirm",
          Payload: {
            BiddingInfoResult: response.items,  //スケジュールデータを作った際に最新の落札確定データを取得してCSVを作成
            PrefectureType: prefectureType,
            CoreLinkFolderPath: systemMaster.coreLinkFolderPath,
            SignageFolderPath: systemMaster.signageFolderPath
          }
        });

        // WebView2側からの完了メッセージを待つ
        await waitForWebViewMessage("Confirm");

        // すべて成功したら onSuccess を実行
        if (options?.onSuccess) {
          options.onSuccess();
        }
      } else {
        throw new Error("専用ブラウザ環境を確認できませんでした。");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "不明なエラー";
      setError(errorMessage);
      // alert("落札確定処理中にエラーが発生しました。" + errorMessage);
    }
    finally {
      finalizeConfirm();
    }
  }, [isConfirm, confirmSchedule, refetchSystemMaster, finalizeConfirm]);

  /**
   * 外部から呼び出すためのハンドラー
   */
  const handleExecuteConfirm = useCallback(async (items: BiddingInfoResult[] | undefined, prefectureType: PrefectureType) => {
    await executeConfirmProcess(items, prefectureType)
  }, [executeConfirmProcess])

  return {
    handleExecuteConfirm,
    isConfirm,
    error
  }
};

export const useBiddingConfirmCancelMutation = (itemFilters:GetItemsFilters, loginId:string | "", options?: { onSuccess?: () => void }) => {
  // 落札確定処理実行中フラグ
  const [isCancel, setCancel] = useState(false);
  // エラー状態管理
  const [error, setError] = useState<string | null>(null);

  // --- 共通終了処理 ---
  const finalizeConfirmCancel = useCallback(() => {
    setCancel(false);
    document.body.style.cursor = 'default';
  }, []);

  const { mutateAsync: ConfirmCancelSchedules } = useBiddingSchedulesConfirmCancelMutation(itemFilters, loginId);

  const executeConfirmCancelProcess = useCallback(async (biddingDate: string | undefined, listingCategory: number | "" | undefined,prefectureType: number | "" | undefined) => {
    if(isCancel) return;
    if(!biddingDate || biddingDate === "" || biddingDate == undefined ||
       !listingCategory || listingCategory === undefined ||
      !prefectureType || prefectureType === undefined) return;

    setCancel(true);
    setError(null);
    document.body.style.cursor = 'wait';

    try{
      if (window.chrome?.webview) {
        const _listingCategory = listingCategory === ListingCategoryConst.PRIVATE ? ListingCategoryConst.PRIVATE :
                                 listingCategory === ListingCategoryConst.BIDDING ? ListingCategoryConst.BIDDING : ListingCategoryConst.SALE;
        const _prefectureType = prefectureType === PrefTypeConst.INTERNAL ? PrefTypeConst.INTERNAL : PrefTypeConst.EXTERNAL;

        // リクエストデータ作成
        const request: BiddingSchedulesRequest = { 
          Date: parseInt(biddingDate),
          ListingCategory: _listingCategory, 
          PrefectureType: _prefectureType
        };

        // API実行
        await ConfirmCancelSchedules(request);

        // 完了処理
        if (options?.onSuccess) {
          options.onSuccess();
        }

      } else {
        throw new Error("専用ブラウザ環境を確認できませんでした。");
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "不明なエラー";
      setError(errorMessage);
    }
    finally {
      finalizeConfirmCancel();
    }

  },[isCancel, ConfirmCancelSchedules, finalizeConfirmCancel]);

  /**
   * 外部から呼び出すためのハンドラー
   */
  const handleExecuteConfirmCancel = useCallback(async (biddingDate: string | undefined, listingCategory: number | "" | undefined, prefectureType: number | "" | undefined) => {
    await executeConfirmCancelProcess(biddingDate, listingCategory, prefectureType)
  }, [executeConfirmCancelProcess])

  return {
    handleExecuteConfirmCancel,
    isCancel,
    error
  }
};

/*
 * CSV再出力
*/
export const useCsvExportOnly = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { refetch: refetchSystemMaster } = useSystemMasterQuery();

  const handleExportCSV = useCallback(async (
    items: BiddingInfoResult[], 
    prefectureType: PrefectureType, 
    csvoutputType: CSVOutputType
  ) => {
    if (isExporting) return;
    if (!items || items.length === 0) {
      toast.error("出力するデータがありません。");
      return;
    }

    if (!window.chrome?.webview) {
      toast.error("専用ブラウザ環境を確認できませんでした。");
      return;
    }

    setIsExporting(true);
    document.body.style.cursor = 'wait';

    try {
      // 1. システムマスタから出力先フォルダを取得
      const { data: systemMaster, isError } = await refetchSystemMaster();
      if (isError || !systemMaster) {
        throw new Error("システムマスタの取得に失敗しました。");
      }

      // 2. 出力タイプに合わせて Payload と PageName を切り替え
      let pageName = "";
      let payload: any = { BiddingInfoResult: items };

      if (csvoutputType === CSVOutputConst.CORE_SYSTEM) {
        if (!systemMaster.coreLinkFolderPath) throw new Error("基幹連携用CSV出力先フォルダが設定されていません。");
        pageName = "CoreLink";
        payload.CoreLinkFolderPath = systemMaster.coreLinkFolderPath;
      } else {
        if (!systemMaster.signageFolderPath) throw new Error("サイネージ用CSV出力先フォルダが設定されていません。");
        pageName = "SignageLink";
        payload.PrefectureType = prefectureType;
        payload.SignageFolderPath = systemMaster.signageFolderPath;
      }

      // 3. C#へ処理を依頼
      window.chrome.webview.postMessage({
        PageName: pageName,
        Function: pageName,
        Payload: payload
      });

      // 4. C#側の完了メッセージを待機
      await waitForWebViewMessage("Success"); 
      
      toast.success("CSVの出力が完了しました。", { duration: 5000 });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "不明なエラー";
      toast.error(`CSV出力に失敗しました: ${errorMessage}`);
    } finally {
      setIsExporting(false);
      document.body.style.cursor = 'default';
    }
  }, [isExporting, refetchSystemMaster]);

  return { handleExportCSV, isExporting };
};

/**
 * 帳票印刷用データ取得処理
 * ボタン押下時に最新の検索条件でデータを取得するためのフック
 * @returns 
 */
export const useBiddingReportPrintMutation = () => {
  const { biddingGetAllResultListForReport } = useItemsApi();

  return useMutation({
    mutationFn: async (filters: GetItemsFilters) => {
      // 空の条件を除外
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v != null && v !== '')
      );
      const queryString = qs.stringify(cleanFilters, { arrayFormat: 'repeat' });
      
      // APIを実行（既存の帳票用APIをそのまま利用）
      const response = await biddingGetAllResultListForReport(queryString); 
      
      // 配列(.items)だけを取り出して返す
      return response.items || [];
    }
  });
};